<template>
  <div id="app" class="min-h-screen bg-gray-50">
    <!-- Loading globale -->
    <LoadingOverlay v-if="isLoading" />
    
    <!-- Layout principale con router-view -->
    <component :is="currentLayout">
      <router-view v-slot="{ Component, route }">
        <transition 
          :name="route.meta.transition || 'fade'" 
          mode="out-in"
          @before-enter="scrollToTop"
        >
          <component :is="Component" :key="route.path" />
        </transition>
      </router-view>
    </component>

    <!-- Modali globali -->
    <ConfirmDialog />
    
    <!-- Notifica offline -->
    <OfflineNotice v-if="!isOnline" />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useAppStore } from '@/stores/app'
import { useOnline } from '@vueuse/core'
import { useBranding } from '@/composables/useBranding'

// Layouts
import DefaultLayout from '@/layouts/DefaultLayout.vue'
import AdminLayout from '@/layouts/AdminLayout.vue'
import POSLayout from '@/layouts/POSLayout.vue'
import AuthLayout from '@/layouts/AuthLayout.vue'
import SuperAdminLayout from '@/layouts/SuperAdminLayout.vue'

// Componenti globali
import LoadingOverlay from '@/components/common/LoadingOverlay.vue'
import ConfirmDialog from '@/components/common/ConfirmDialog.vue'
import OfflineNotice from '@/components/common/OfflineNotice.vue'

// Stores
const authStore = useAuthStore()
const appStore = useAppStore()
const router = useRouter()
const route = useRoute()

// Branding
const { loadBranding, applyBranding } = useBranding()

// State
const isOnline = useOnline()
const isLoading = computed(() => appStore.isLoading)

// Layout dinamico basato sulla route
const layouts = {
  default: DefaultLayout,
  admin: AdminLayout,
  pos: POSLayout,
  auth: AuthLayout,
  superadmin: SuperAdminLayout
}

const currentLayout = computed(() => {
  const layoutName = route.meta.layout || 'default'
  return layouts[layoutName] || DefaultLayout
})

// Funzione per scrollare in alto
const scrollToTop = () => {
  window.scrollTo({ top: 0, behavior: 'instant' })
}

// Gestione tema scuro
const initTheme = () => {
  const savedTheme = localStorage.getItem('theme')
  const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
  
  if (savedTheme === 'dark' || (!savedTheme && prefersDark)) {
    document.documentElement.classList.add('dark')
    appStore.setTheme('dark')
  } else {
    document.documentElement.classList.remove('dark')
    appStore.setTheme('light')
  }
}

// Watch per cambio tema
watch(() => appStore.theme, (newTheme) => {
  if (newTheme === 'dark') {
    document.documentElement.classList.add('dark')
  } else {
    document.documentElement.classList.remove('dark')
  }
  localStorage.setItem('theme', newTheme)
})

// Gestione resize per mobile detection
const handleResize = () => {
  appStore.setIsMobile(window.innerWidth < 768)
}

// Lifecycle hooks
onMounted(async () => {
  try {
    initTheme()
    handleResize()
    window.addEventListener('resize', handleResize)

    console.log('App mounted, auth status:', authStore.isAuthenticated)

    // L'autenticazione è già inizializzata automaticamente nel store

    // Verifica autenticazione
    if (authStore.isAuthenticated) {
        console.log('User authenticated, ready to go')
    }

    // Load tenant branding
    // First check if custom domain is being used
    const hostname = window.location.hostname
    if (hostname !== 'localhost' && hostname !== '127.0.0.1' && !hostname.includes('torteonline.com')) {
      // Custom domain detected, load branding by domain
      console.log('Custom domain detected:', hostname)
      // This will be handled by the domain detection logic
    } else if (authStore.user?.tenantId) {
      // Load branding for authenticated tenant
      await loadBranding(authStore.user.tenantId)
    }
  } catch (error) {
    console.error('App initialization error:', error)
  }
})

onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
})

// Gestione errori router
router.onError((error) => {
  console.error('Router error:', error)
  appStore.showError('Si è verificato un errore di navigazione')
})
</script>

<style>
/* Transizioni per le route */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-enter-active,
.slide-leave-active {
  transition: all 0.3s ease;
}

.slide-enter-from {
  transform: translateX(100%);
  opacity: 0;
}

.slide-leave-to {
  transform: translateX(-100%);
  opacity: 0;
}

/* Animazione per loading */
@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.animate-spin {
  animation: spin 1s linear infinite;
}

/* Stili per stampa */
@media print {
  .no-print {
    display: none !important;
  }
  
  .print-only {
    display: block !important;
  }
}

/* Fix per iOS Safari */
@supports (-webkit-touch-callout: none) {
  .min-h-screen {
    min-height: -webkit-fill-available;
  }
}
</style>
